import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useWebServer } from "../../../../../providers";
import { ianaTimezone } from "../../../../../utils/dataDownload";
import { toModel } from "../PivotTable/pivotModel";
import { DATE_PRESETS, DEFAULT_FILTERS, toRequestFilter } from "../Filters/filterState";
import { useFirstReportDate } from "../Filters/useReportFields";

// The species map's question: species counted per collection site.
export const SPECIES_MAP_PIVOT = { rows: ["collectionSite"], columns: ["species"] };

// the periods the map offers; it opens on the last 30 days
export const MAP_PERIODS = DATE_PRESETS.filter((p) => p.key !== "lastYear");
export const DEFAULT_PERIOD = "last30";

/** The report filters for a period ending today. */
export const periodFilters = (period, today = new Date(), firstDate = null) => {
  const preset = MAP_PERIODS.find((p) => p.key === period) ?? MAP_PERIODS.find((p) => p.key === DEFAULT_PERIOD);
  return { ...DEFAULT_FILTERS, dates: { field: "report_date", ...preset.range(today, firstDate) } };
};

/**
 * The species map's counts for the chosen period, loaded while `enabled`
 * (the main map shows the Reports section). Returns {model, filters,
 * loading, error, period, setPeriod}; the last map stays while the next loads.
 */
export const useSpeciesMap = (enabled) => {
  const { sendOrganizationRequest } = useWebServer();
  const timeZone = useSelector((state) => state.user?.timeZone);
  const firstReportDate = useFirstReportDate();
  const [period, setPeriod] = useState(DEFAULT_PERIOD);
  const [state, setState] = useState({ model: null, filters: null, loading: false, error: null });

  useEffect(() => {
    if (!enabled) return undefined;
    let current = true;
    const tz = ianaTimezone(timeZone);
    setState((s) => ({ ...s, loading: true, error: null }));
    (async () => {
      try {
        const first = period === "allTime" ? await firstReportDate() : null;
        const filters = periodFilters(period, new Date(), first);
        const filter = toRequestFilter(filters, tz);
        const res = await sendOrganizationRequest("post")({
          url: "/organization/reports/pivot",
          data: { ...SPECIES_MAP_PIVOT, filter, timezone: tz },
        });
        if (current) setState({ model: toModel(res.data), filters, loading: false, error: null });
      } catch (err) {
        if (!current) return;
        setState((s) => ({
          ...s,
          loading: false,
          error: err?.response?.data?.error || "The species map could not be loaded. Check your connection and try again.",
        }));
      }
    })();
    return () => {
      current = false;
    };
    // the provider rebuilds its request helpers on every render; the map
    // depends only on whether it is shown, the period and the timezone
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, period, timeZone]);

  return { ...state, period, setPeriod };
};
